import { Box, useColorMode } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Portfolio from './components/Portfolio';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import ChristmasBooking from './components/ChristmasBooking';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import PrivacyPolicy from './pages/PrivacyPolicy';
import './darkMode.css';
import Pricing from './components/Pricing';
import { isChristmasActive } from './data/christmas';
import { SHOW_FAQ } from './data/navigation';
import { API_URL } from './api';

// Hook to trigger .reveal animations when elements enter the viewport
function useScrollReveal() {
  useEffect(() => {
    let observer: IntersectionObserver;

    const init = () => {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Atribut, ne třída: při přepnutí světlý/tmavý režim React
              // přepíše className (Chakra mění vygenerovanou třídu) a odhalený
              // obsah by zmizel. Atribut, který není v JSX, React nechává být.
              entry.target.setAttribute('data-revealed', '');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
      );

      document.querySelectorAll('.reveal, .section-heading-line').forEach((el) => {
        observer.observe(el);
      });
    };

    // Short delay to ensure DOM is fully painted after React render
    const timer = setTimeout(init, 100);

    return () => {
      clearTimeout(timer);
      observer?.disconnect();
    };
  }, []);
}

// Jak dlouho po načtení držet sekci z odkazu na místě (viz useScrollToHash)
const HASH_SETTLE_MS = 8000;
// Cokoli z toho = návštěvník posouvá sám, dál do toho nezasahujeme
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

// Odkaz přímo na sekci (např. hrachovenachova.cz/#vanoce sdílený na Instagramu):
// prohlížeč kotvu hledá hned po načtení, kdy React ještě nic nevykreslil,
// a nenajde ji. Po vykreslení to proto zopakujeme sami.
function useScrollToHash() {
  useEffect(() => {
    let id = '';
    try {
      id = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return;
    }
    if (!id) return;

    const scrollToTarget = () => {
      document.getElementById(id)?.scrollIntoView({ block: 'start' });
    };

    // Krátké zpoždění – ať se stihne usadit rozložení stránky
    const timer = window.setTimeout(scrollToTarget, 150);

    // Obsah nad cílem ještě chvíli roste (vánoční termíny se načítají z API,
    // dobíhají fotky). Chrome a Firefox si pozici udrží samy, Safari ne – cíl
    // by odjel o kus níž. Proto při každé změně výšky stránky posun dorovnáme,
    // dokud návštěvník nezačne posouvat sám, nejdéle HASH_SETTLE_MS.
    let observer: ResizeObserver | null = null;
    const stop = () => {
      window.clearTimeout(timer);
      observer?.disconnect();
      observer = null;
      USER_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop));
    };
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scrollToTarget);
      observer.observe(document.body);
      USER_SCROLL_EVENTS.forEach((type) =>
        window.addEventListener(type, stop, { passive: true })
      );
    }
    const settleTimer = window.setTimeout(stop, HASH_SETTLE_MS);

    return () => {
      window.clearTimeout(settleTimer);
      stop();
    };
  }, []);
}

// Component for the main homepage
// Musí být mimo App: kdyby byla definovaná uvnitř, vznikl by při každém
// překreslení (např. přepnutí světlý/tmavý režim) nový typ komponenty,
// React by celou stránku odmountoval a znovu vytvořil – a scroll animace
// by zůstaly viset na neviditelném obsahu.
const HomePage = () => (
  <>
    <Navbar />
    <Hero />
    {/* Po akci se sekce sama skryje (datum v src/data/christmas.ts) */}
    {isChristmasActive() && <ChristmasBooking />}
    <Portfolio />
    <About />
    <Pricing />
    {/* Zapíná se v src/data/navigation.ts (spolu s odkazem DOTAZY v menu) */}
    {SHOW_FAQ && <FAQ />}
    <Contact />
    <Footer />
  </>
);

// Administrace rezervací běží na backendu (api.hrachovenachova.cz/admin, lokálně
// localhost:3001/admin). Adresa webu /admin na ni jen přesměruje – Eva si tak
// pamatuje jedno místo a nenarazí na prázdnou stránku.
const AdminRedirect = () => {
  useEffect(() => {
    window.location.replace(`${API_URL}/admin`);
  }, []);
  return (
    <Box p={10} textAlign="center" fontWeight={300}>
      Přesměrovávám do administrace rezervací…
    </Box>
  );
};

function App() {
  const { colorMode } = useColorMode();
  useScrollReveal();
  useScrollToHash();

  return (
    <Router>
      <Box
        bg={colorMode === 'dark' ? 'gray.900' : 'white'}
        color={colorMode === 'dark' ? 'white' : 'black'}
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/admin" element={<AdminRedirect />} />
          {/* Neznámá adresa → úvodní stránka místo prázdné obrazovky */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieConsent />
      </Box>
    </Router>
  );
}

export default App;
